import { ReactNode } from "react";

import styles from "./cappedText.module.css";

// Beyond this size, rendering text as a single DOM node produces an element
// tens-of-thousands to millions of pixels tall. The browser then lays out,
// paints, and (most expensively) *layerizes* that whole node on every resize —
// a single resize of a ~1,000,000px node was measured at ~14s of `Layerize`,
// wedging the main thread (laggy in Blink, spinlocks WebKit). A fixed-height
// scroller does not help: the off-screen content is still layerized. The only
// reliable fix is to keep the giant node out of the DOM entirely, so we render
// a head slice and a static notice for the remainder.
const MAX_INLINE_CHARS = 250_000;

interface CappedText {
  /** The text to render: a head slice when capped, the full text otherwise. */
  text: string;
  /** Whether the text was truncated. */
  truncated: boolean;
  /** A static notice to render after the body, or null when not truncated. */
  notice: ReactNode;
}

/**
 * Caps very large text so a single huge DOM node never enters the layout/paint
 * path. Returns a (possibly truncated) string and a static notice describing
 * how much was hidden. Intentionally offers no expand affordance — revealing
 * the full content would reintroduce the performance cliff.
 */
export const cappedText = (full: string): CappedText => {
  if (full.length <= MAX_INLINE_CHARS) {
    return { text: full, truncated: false, notice: null };
  }
  const text = full.slice(0, MAX_INLINE_CHARS);
  const hidden = full.length - text.length;
  return {
    text,
    truncated: true,
    notice: (
      <div className={styles.notice}>
        {`Output truncated — ${formatSize(hidden)} hidden`}
      </div>
    ),
  };
};

const formatSize = (n: number): string => {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1)} MB`;
  }
  if (n >= 1_000) {
    return `${Math.round(n / 1_000)} KB`;
  }
  return `${n} chars`;
};
